import axios, { AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';

import { AuthenticationStatus, useTokenStore } from '@/stores/token';
import { OpenAPI } from '@/openapi';
import globalConfig from '@/plugins/global-config';
import { hasLogging } from '@/plugins/global-config';

import { eventBus } from './eventBus';

export const refreshTokens = () => {};

const setAuthenticationHeader = (config: InternalAxiosRequestConfig, token: string) => {
  config.headers = config.headers || {};
  config.headers['Authorization'] = `Bearer ${token}`;
  return config;
};

export const configureAxios = () => {
  OpenAPI.BASE = globalConfig.API_URL;
  OpenAPI.CREDENTIALS = 'include';
  OpenAPI.WITH_CREDENTIALS = true;
  const tokenStore = useTokenStore();
  const MAX_RETRIES = 3;

  axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const { accessToken, isAuthenticated: isAuthenticatedFn } = tokenStore;
    const isAuthenticated = isAuthenticatedFn(accessToken ?? undefined); // check if token is valid

    switch (isAuthenticated) {
      case AuthenticationStatus.Invalid:
      case AuthenticationStatus.NotAuthenticated:
        eventBus.emit('error401');

        return Promise.reject(new AxiosError('User is not authenticated'));
      case AuthenticationStatus.Expired:
        if (!accessToken) {
          return Promise.reject(new AxiosError('No access token available'));
        }

        config = setAuthenticationHeader(config, accessToken);

        if (config.url?.endsWith('auth/refresh-token')) {
          return config;
        }

        void tokenStore.refreshTokenFromServer();

        return config;
      case AuthenticationStatus.Authenticated:
      default:
        if (!accessToken) {
          return Promise.reject(new AxiosError('No access token available'));
        }

        config = setAuthenticationHeader(config, accessToken);

        if (config.url?.includes('download')) {
          config.responseType = 'blob';
        }

        return config;
    }
  });

  // token-refresh-required
  axios.interceptors.response.use(
    (response) => response,
    (err: AxiosError) => {
      const config = err.config as AxiosRequestConfig & {
        _retry: number;
      };

      /* route forbidden/not allowed */
      if (err.response?.status === 403) {
        // Special handling for refresh token failures - redirect to entralogin
        if (config?.url?.endsWith('auth/refresh-token')) {
          eventBus.emit('error401'); // triggers redirect to entralogin
          return Promise.reject(err);
        }

        eventBus.emit('error403', {
          url: config?.url,
          method: config?.method,
        });
      }

      /* token expired / invalid */
      if (err.response?.status === 401) {
        config._retry = config._retry ?? 0;

        if (config._retry >= MAX_RETRIES) {
          hasLogging('sey-auth') && console.log('[sey-auth] 401: maximum retries reached');
          eventBus.emit('error401'); // will clear token and redirect elsewhere
          return Promise.reject(err);
        }

        config._retry += 1;

        try {
          return axios(config);
        } catch (_error) {
          return Promise.reject(err instanceof Error ? err : new Error(String(err)));
        }
      }

      if (err.response?.status === 400) {
        eventBus.emit('error400', {
          message: (err.response?.data as { message: string }).message || 'UNKNOWN_ERROR',
        });
      }

      if (err.response?.status === 404) {
        eventBus.emit('error404', {
          message: (err.response?.data as { message: string }).message || 'UNKNOWN_ERROR',
        });
      }

      if (err.response?.status === 409) {
        const data = err.response?.data as any;

        // Get message from various possible forms
        const raw =
          (Array.isArray(data?.errors) && (data.errors[0]?.key || data.errors[0]?.message)) ||
          data?.message ||
          (typeof data === 'string' ? data : 'CONFLICT');

        // Prisma/Unique-Constraint freundlich benennen
        const message =
          /constraint violation/i.test(raw) || data?.code === 'P2002'
            ? 'Constraint violation exception'
            : raw;

        eventBus.emit('error409', { message });
      }

      if (err.response?.status === 410) {
        eventBus.emit('error410');
      }

      if (err.response?.status === 500) {
        eventBus.emit('error500', {
          message: (err.response?.data as { message: string }).message || 'UNKNOWN_ERROR',
        });
      }

      return Promise.reject(err instanceof Error ? err : new Error(String(err)));
    },
  );
};
